import React, { useState } from 'react';
import styled from 'styled-components';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';
import { FraudReport, ResolvedReason } from 'fraud/types';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { UseMutateFunction } from '@tanstack/react-query';

const t = withLocalePrefix('fraud.resolve_fraud_report_modal');

const ReasonsDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing['1']};
  padding-top: ${Tokens.spacing['2']};
  padding-bottom: ${Tokens.spacing['2']};
`;

type ResolveFraudReportModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  onResolve: UseMutateFunction<
    FraudReport,
    Error,
    {
      resolved_reason_ids: number[];
      other_text?: string | null;
    },
    unknown
  >;
  resolvedReasons: ResolvedReason[];
  isPending?: boolean;
};

const ResolveFraudReportModal = ({
  isOpen,
  closeModal,
  onResolve,
  resolvedReasons,
  isPending = false,
}: ResolveFraudReportModalProps) => {
  const [selectedReasonIds, setSelectedReasonIds] = useState<number[]>([]);
  const [otherText, setOtherText] = useState<string>('');
  const [otherReasonSelected, setOtherReasonSelected] =
    useState<boolean>(false);

  const handleReasonSelection = (reason: ResolvedReason) => {
    setSelectedReasonIds((prev) =>
      prev.includes(reason.id)
        ? prev.filter((id) => id !== reason.id)
        : [...prev, reason.id]
    );

    if (reason.name === 'other') {
      if (otherReasonSelected) {
        setOtherText('');
      }
      setOtherReasonSelected(!otherReasonSelected);
    }
  };

  const handleDismiss = () => {
    setSelectedReasonIds([]);
    setOtherText('');
    setOtherReasonSelected(false);
    closeModal();
  };

  const handleSubmit = () => {
    onResolve(
      {
        resolved_reason_ids: selectedReasonIds,
        other_text: otherText || null,
      },
      {
        onSuccess: () => {
          handleDismiss();
        },
      }
    );
  };

  if (!resolvedReasons) {
    return null;
  }

  return (
    <DataEntryModal
      size={'medium'}
      isOpen={isOpen}
      onDismiss={handleDismiss}
      onSubmit={handleSubmit}
      ariaLabel={t('title')}
      data-provides="resolve-fraud-report-modal"
    >
      <DataEntryModal.Header onClick={handleDismiss}>
        {t('title')}
      </DataEntryModal.Header>

      <DataEntryModal.Body>
        <Stack spacing={2}>
          <Body>{t('description')}</Body>
          <ReasonsDiv data-provides="resolve-report-reasons">
            <SecondaryBody medium>{t('body')}</SecondaryBody>
            {resolvedReasons?.map((reason: ResolvedReason) => (
              <Checkbox
                key={reason.id}
                checked={selectedReasonIds.includes(reason.id)}
                label={t(`resolved_reason_types.${reason.name}`)}
                onChange={() => handleReasonSelection(reason)}
                value={reason.name}
                data-provides={`resolve-report-reason-${reason.name}`}
              />
            ))}
            {otherReasonSelected && (
              <TextInput
                id="resolve_reason_other_text"
                label=""
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                data-provides="resolve_reason_other_text"
              />
            )}
          </ReasonsDiv>
        </Stack>
      </DataEntryModal.Body>

      <DataEntryModal.Footer>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <SecondaryButton
            onClick={handleDismiss}
            disabled={isPending}
            data-provides="resolve-report-cancel"
          >
            {t('cancel')}
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={isPending || selectedReasonIds.length === 0}
            data-provides="resolve-report-submit"
          >
            {t('submit')}
          </PrimaryButton>
        </Stack>
      </DataEntryModal.Footer>
    </DataEntryModal>
  );
};

export default ResolveFraudReportModal;
